import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, Variants } from "framer-motion";
import { useEffect, useState } from "react";
import Logo from "../../assets/Logo-removebg-preview.png";

/* =========================================================
   TYPES
========================================================= */
interface NavigationBarProps {
  hasSkipped: boolean;
}

interface NavItem {
  label: string;
  path: string;
  children?: { label: string; path: string }[];
}

/* =========================================================
   MOTION CONSTANTS
========================================================= */
const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];

/* =========================================================
   ANIMATION VARIANTS
========================================================= */
const logoVariant: Variants = {
  hidden: { opacity: 0, y: -50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.5, ease: EASE_OUT },
  },
};

const navContainerVariant: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15, delayChildren: 0.3 },
  },
};

const navItemVariant: Variants = {
  hidden: { opacity: 0, y: -40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: EASE_OUT },
  },
};

/* =========================================================
   NAV LINKS
========================================================= */
const LINKS: NavItem[] = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about-us" },
  { label: "BPO", path: "/bpo" },
  {
    label: "Web Development",
    path: "/web-development",

  },
  { label: "Address Finder", path: "/address-finder" },
];

/* =========================================================
   COMPONENT
========================================================= */
const NavigationBar: React.FC<NavigationBarProps> = ({ hasSkipped }) => {
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Show navbar (skip aware)
  useEffect(() => {
    if (hasSkipped) {
      setShowNavbar(true);
      setShouldAnimate(true);
      return;
    }

    const timer = setTimeout(() => {
      setShowNavbar(true);
      setShouldAnimate(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, [hasSkipped]);

  if (!showNavbar) return null;

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: EASE_OUT }}
      className="sticky top-0 z-[102] bg-white"
    >
      <div className="max-w-full mx-auto px-6">
        <nav className="flex h-16 items-center justify-between">
          {/* LOGO */}
          <motion.div
            variants={logoVariant}
            initial="hidden"
            animate={shouldAnimate ? "visible" : "hidden"}
          >
            <Link to="/">
              <img src={Logo} alt="Company logo" className="h-14" />
            </Link>
          </motion.div>

          {/* DESKTOP NAV */}
          <motion.ul
            className="hidden md:flex gap-8 text-sm font-medium"
            variants={navContainerVariant}
            initial="hidden"
            animate={shouldAnimate ? "visible" : "hidden"}
          >
            {LINKS.map(({ label, path, children }) => (
              <motion.li
                key={path}
                variants={navItemVariant}
                className="relative group"
              >
                {/* PARENT LINK */}
             <NavLink
                to={path}
                end
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                    `block py-2 text-sm transition ${
                    isActive
                        ? "text-blue-600 font-semibold"
                        : "text-gray-700 hover:text-blue-600"
                    }`
                }
                >
                {label}
                </NavLink>


                {/* DROPDOWN */}
                {children && (
                  <div
                    className="
                      absolute left-0 top-full  w-48
                      rounded-lg border bg-white shadow-lg
                      opacity-0 invisible
                      group-hover:opacity-100 group-hover:visible
                      group-focus-within:opacity-100 group-focus-within:visible
                      transition
                    "
                  >
                    <ul className="py-2">
                      {children.map((child) => (
                        <li key={child.path}>
                          <NavLink
                            to={child.path}
                            className={({ isActive }) =>
                              `block px-4 py-2 text-sm transition ${
                                isActive
                                  ? "text-blue-600 bg-blue-50 font-medium"
                                  : "text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                              }`
                            }
                          >
                            {child.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.li>
            ))}
          </motion.ul>

          {/* HAMBURGER */}
          <button
            className="md:hidden flex flex-col gap-1.5"
            onClick={() => setIsOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            <span className="w-6 h-[2px] bg-black" />
            <span className="w-6 h-[2px] bg-black" />
            <span className="w-6 h-[2px] bg-black" />
          </button>
        </nav>
      </div>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            className="md:hidden bg-white shadow-md px-6 pb-6"
          >
            {LINKS.map(({ label, path, children }) => (
              <div key={path} className="py-1">
                <NavLink
                  to={path}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `block py-2 text-sm transition ${
                      isActive
                        ? "text-blue-600 font-semibold"
                        : "text-gray-700 hover:text-blue-600"
                    }`
                  }
                >
                  {label}
                </NavLink>

                {children && (
                  <div className="ml-4 mt-1 flex flex-col">
                    {children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        onClick={() => setIsOpen(false)}
                        className={({ isActive }) =>
                          `py-1 text-sm transition ${
                            isActive
                              ? "text-blue-600 font-medium"
                              : "text-gray-600 hover:text-blue-600"
                          }`
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default NavigationBar;
